/**
 * Modal reutilizable de replay de sesiones.
 *
 * Estrategia de reproducción:
 *   - 2 `<audio>` reusables (user + assistant) creados como JSX (refs estables).
 *     Reusarlos garantiza que la user-activation del click "Reproducir" se
 *     conserve para todos los .play() futuros (los navegadores la atan al
 *     elemento HTMLMediaElement, no a la sesión global).
 *   - Pre-fetch de cada audio file como Blob → ObjectURL al abrir el modal.
 *     Así el `.play()` en runtime es instantáneo (sin red, sin race).
 *   - Texto: visible turn-by-turn según `startedAt`/`endedAt` reales. Audio:
 *     se dispara cuando el cronómetro cruza el `showAtMs` del evento.
 *   - Fallback Web Speech sólo cuando no hay audio persistido para un turn.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchAudioManifest,
  fetchTranscript,
  turnAudioUrl,
  type AudioManifestItem,
  type TranscriptTurn,
} from '../../api/admin.js';
import { GATED_CONTENT_PLACEHOLDER, isGatedModuleError } from '../../lib/privacy.js';

export interface SessionReplayModalProps {
  open: boolean;
  sessionId: string;
  onClose: () => void;
}

interface ReplayEvent {
  kind: 'user' | 'assistant';
  turnIndex: number;
  text: string;
  showAtMs: number;
  speakMs: number;
  /** Epoch ms absoluto del momento real del evento (para mostrar fecha y hora). */
  atMs: number;
  audioUrl?: string;
  audioDurationMs?: number;
}

/** Fecha + hora absoluta de un evento, en formato local es-AR (24h). */
function formatStamp(ms: number): string {
  return new Date(ms).toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

const WPM = 165;
const MS_PER_CHAR = 60_000 / (WPM * 5);

function estimateSpeakMs(text: string): number {
  return Math.max(1200, Math.round(text.length * MS_PER_CHAR));
}

function buildEvents(
  turns: readonly TranscriptTurn[],
  manifest: readonly AudioManifestItem[],
  sessionId: string,
): ReplayEvent[] {
  if (turns.length === 0) return [];
  const t0Iso = turns[0]?.startedAt;
  if (!t0Iso) return [];
  const t0 = new Date(t0Iso).getTime();

  const manifestByKey = new Map<string, AudioManifestItem>();
  for (const m of manifest) {
    manifestByKey.set(`${m.turnIndex}:${m.kind}`, m);
  }

  const events: ReplayEvent[] = [];
  for (const turn of turns) {
    const turnStart = new Date(turn.startedAt).getTime() - t0;
    const turnEnd = new Date(turn.endedAt).getTime() - t0;
    if (turn.userText && turn.userText.trim().length > 0) {
      const audio = manifestByKey.get(`${turn.turnIndex}:user`);
      events.push({
        kind: 'user',
        turnIndex: turn.turnIndex,
        text: turn.userText.trim(),
        showAtMs: Math.max(0, turnStart),
        atMs: new Date(turn.startedAt).getTime(),
        speakMs: audio?.durationMs ?? estimateSpeakMs(turn.userText.trim()),
        ...(audio ? { audioUrl: turnAudioUrl(sessionId, turn.turnIndex, 'user') } : {}),
      });
    }
    if (turn.assistantText && turn.assistantText.trim().length > 0) {
      const offsetFromTurnStart =
        turn.timeToFirstAudioMs ?? Math.round(turn.durationMs * 0.35);
      const assistantShow = Math.min(turnEnd, turnStart + offsetFromTurnStart);
      const audio = manifestByKey.get(`${turn.turnIndex}:assistant`);
      events.push({
        kind: 'assistant',
        turnIndex: turn.turnIndex,
        text: turn.assistantText.trim(),
        showAtMs: Math.max(0, assistantShow),
        atMs: t0 + Math.max(0, assistantShow),
        speakMs: audio?.durationMs ?? estimateSpeakMs(turn.assistantText.trim()),
        ...(audio ? { audioUrl: turnAudioUrl(sessionId, turn.turnIndex, 'assistant') } : {}),
      });
    }
  }
  return events.sort((a, b) => a.showAtMs - b.showAtMs);
}

function hasSpeechSynthesis(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

interface BlobMap {
  // key = `${kind}:${turnIndex}` → ObjectURL local (blob:...)
  [key: string]: string;
}

export function SessionReplayModal(props: SessionReplayModalProps): ReactElement | null {
  const { open, sessionId, onClose } = props;

  const transcriptQuery = useQuery({
    queryKey: ['admin', 'transcript', sessionId],
    queryFn: () => fetchTranscript(sessionId),
    enabled: open && Boolean(sessionId),
  });

  const manifestQuery = useQuery({
    queryKey: ['admin', 'audio-manifest', sessionId],
    queryFn: () => fetchAudioManifest(sessionId),
    enabled: open && Boolean(sessionId),
  });

  const events = useMemo<ReplayEvent[]>(() => {
    if (!transcriptQuery.data) return [];
    return buildEvents(
      transcriptQuery.data.turns,
      manifestQuery.data?.items ?? [],
      sessionId,
    );
  }, [transcriptQuery.data, manifestQuery.data, sessionId]);

  const hasAnyRealAudio = useMemo(
    () => (manifestQuery.data?.items.length ?? 0) > 0,
    [manifestQuery.data],
  );
  const [playing, setPlaying] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [blobs, setBlobs] = useState<BlobMap>({});
  // Cuántos items del manifest ya fueron *intentados* (resueltos: ok o fallidos).
  // El gating de `stillFetching` se basa en esto, no en los blobs exitosos, para
  // que un fetch que falla no deje el botón Reproducir trabado indefinidamente.
  const [settledBlobs, setSettledBlobs] = useState(0);

  const userAudioRef = useRef<HTMLAudioElement | null>(null);
  const assistantAudioRef = useRef<HTMLAudioElement | null>(null);
  const transcriptEndRef = useRef<HTMLDivElement | null>(null);
  // Los `<audio>` se desbloquean (user-activation) una sola vez en el primer
  // "Reproducir". Reusarlos preserva ese permiso para todos los .play()
  // futuros, así que NO hay que re-desbloquear al "Continuar" — hacerlo
  // reasignaba blobs arbitrarios y dejaba audio espurio sonando.
  const unlockedRef = useRef(false);
  // Idx del evento actualmente cargado en su `<audio>`. Si coincide con
  // `currentIdx` al re-correr el efecto, es un RESUME (pausa→continuar): no
  // reseteamos `currentTime`, así sigue desde donde se pausó. Si difiere, es
  // un evento nuevo (avance natural o salto manual) → carga desde cero.
  const loadedIdxRef = useRef<number>(-1);
  // Cronometría del fallback TTS (sin audio real) para poder pausar/resumir
  // sin reiniciar la utterance. En pasos con audio real quedan en null.
  const ttsStepStartRef = useRef<number | null>(null);
  const ttsStepDurationRef = useRef<number | null>(null);
  const ttsRemainingRef = useRef<number | null>(null);

  const supportsTts = hasSpeechSynthesis();

  // Pre-fetch de todos los audios como Blob → ObjectURL. Eso resuelve
  // dos problemas a la vez: (1) la cookie va con el fetch igual que con
  // cualquier API call, (2) el `<audio>` reproduce desde memoria sin red,
  // así el .play() del raf es instantáneo y no hay race con autoplay.
  useEffect(() => {
    if (!open) return;
    const manifest = manifestQuery.data?.items;
    if (!manifest || manifest.length === 0) return;
    const created: string[] = [];
    const next: BlobMap = {};
    let cancelled = false;
    setSettledBlobs(0);

    void (async () => {
      for (const item of manifest) {
        const key = `${item.kind}:${item.turnIndex}`;
        try {
          const res = await fetch(turnAudioUrl(sessionId, item.turnIndex, item.kind), {
            credentials: 'include',
          });
          if (!res.ok) continue;
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          created.push(url);
          next[key] = url;
          setBlobs((prev) => ({ ...prev, [key]: url }));
        } catch (err) {
          console.warn('[replay] blob fetch failed', err);
        } finally {
          // Contamos el intento aunque haya fallado (non-ok / throw): el botón
          // Reproducir destraba cuando TODOS los items settle-aron, no sólo los
          // exitosos. Un único fetch fallido (5xx/timeout) ya NO deja Play
          // deshabilitado para siempre; ese turn cae al fallback TTS.
          if (!cancelled) setSettledBlobs((n) => n + 1);
        }
      }
    })();

    return () => {
      cancelled = true;
      for (const url of created) URL.revokeObjectURL(url);
    };
  }, [open, sessionId, manifestQuery.data]);

  // ESC para cerrar.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Reset cuando abre/cierra o cambia la sesión.
  useEffect(() => {
    if (!open) {
      setPlaying(false);
      setCurrentIdx(0);
      setBlobs({});
      setSettledBlobs(0);
      unlockedRef.current = false;
      loadedIdxRef.current = -1;
      ttsStepStartRef.current = null;
      ttsStepDurationRef.current = null;
      ttsRemainingRef.current = null;
      stopAudio(userAudioRef.current);
      stopAudio(assistantAudioRef.current);
      if (supportsTts) window.speechSynthesis.cancel();
    }
  }, [open, sessionId, supportsTts]);

  // Replay secuencial: reproduce un evento por vez y avanza apenas termina su
  // audio (sin gaps artificiales — la prioridad es escuchar rápido). Soporta:
  //   - RESUME exacto: pausar/continuar sigue desde el `currentTime` real del
  //     audio, no desde el principio (detectado vía `loadedIdxRef`).
  //   - SALTOS: cambiar `currentIdx` (botones ‹ ›, click en burbuja) recarga el
  //     evento destino desde cero.
  useEffect(() => {
    if (!playing) return;
    if (currentIdx >= events.length) {
      setPlaying(false);
      return;
    }
    const ev = events[currentIdx];
    if (!ev) return;

    const isResume = loadedIdxRef.current === currentIdx;
    loadedIdxRef.current = currentIdx;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let onEnded: (() => void) | null = null;
    let activeEl: HTMLAudioElement | null = null;
    let isTtsStep = false;

    const advance = (): void => {
      if (cancelled) return;
      setCurrentIdx((i) => i + 1);
    };

    const blobUrl = ev.audioUrl ? blobs[`${ev.kind}:${ev.turnIndex}`] : undefined;
    const el = ev.kind === 'user' ? userAudioRef.current : assistantAudioRef.current;

    if (el && blobUrl) {
      // Paso con audio real: limpiamos cronometría TTS para que el cleanup no
      // intente pausar/guardar una utterance inexistente.
      ttsStepStartRef.current = null;
      ttsStepDurationRef.current = null;
      ttsRemainingRef.current = null;
      activeEl = el;
      if (!isResume || el.src !== blobUrl) {
        el.src = blobUrl;
        el.currentTime = 0;
      }
      onEnded = (): void => {
        if (onEnded) el.removeEventListener('ended', onEnded);
        advance();
      };
      el.addEventListener('ended', onEnded);
      const p = el.play();
      if (typeof p?.catch === 'function') {
        p.catch((err) => {

          console.warn('[replay] audio play failed, falling back to TTS', err);
          if (onEnded) {
            el.removeEventListener('ended', onEnded);
            onEnded = null;
          }
          if (supportsTts) speakFallback(ev);
          timer = setTimeout(advance, ev.speakMs);
        });
      }
    } else {
      // Sin audio real — fallback Web Speech. En resume reanudamos la utterance
      // pausada y esperamos sólo lo que faltaba; fresh arranca de cero.
      isTtsStep = true;
      let waitMs = ev.speakMs;
      if (isResume && ttsRemainingRef.current != null) {
        waitMs = ttsRemainingRef.current;
        if (supportsTts) window.speechSynthesis.resume();
      } else if (supportsTts) {
        window.speechSynthesis.cancel();
        speakFallback(ev);
      }
      ttsStepStartRef.current = performance.now();
      ttsStepDurationRef.current = waitMs;
      ttsRemainingRef.current = null;
      timer = setTimeout(advance, waitMs);
    }

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      if (activeEl && onEnded) activeEl.removeEventListener('ended', onEnded);
      // Pausar SIN resetear: el `currentTime` del audio queda intacto para el
      // próximo "Continuar". Para TTS guardamos lo que faltaba y pausamos la
      // utterance (no cancel) sólo si el paso actual fue TTS — así no queda
      // voz colgada cruzándose con el audio real al reanudar.
      pauseAudio(userAudioRef.current);
      pauseAudio(assistantAudioRef.current);
      if (isTtsStep && supportsTts) {
        if (ttsStepStartRef.current != null && ttsStepDurationRef.current != null) {
          const elapsed = performance.now() - ttsStepStartRef.current;
          ttsRemainingRef.current = Math.max(0, ttsStepDurationRef.current - elapsed);
        }
        window.speechSynthesis.pause();
      }
    };
  }, [playing, currentIdx, events, blobs, supportsTts]);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [currentIdx]);

  // Unlock ambos `<audio>` dentro del gesture handler, SÓLO la primera vez.
  // Asignamos un blob dummy a cada uno y play+pause silent para marcarlos
  // como user-activated; después limpiamos el src para no dejar el blob
  // espurio pegado al elemento equivocado. El permiso queda atado al elemento,
  // así que en "Continuar"/saltos no hace falta re-desbloquear (reasignar
  // blobs causaba el bug de la voz cruzada).
  const ensureUnlocked = useCallback(() => {
    if (unlockedRef.current) return;
    const someBlob = Object.values(blobs)[0];
    [userAudioRef.current, assistantAudioRef.current].forEach((el) => {
      if (!el) return;
      el.muted = true;
      if (someBlob) el.src = someBlob;
      const p = el.play();
      const reset = (): void => {
        el.pause();
        el.currentTime = 0;
        el.muted = false;
        el.removeAttribute('src');
        el.load();
      };
      if (typeof p?.then === 'function') {
        p.then(reset).catch(() => {
          el.muted = false;
        });
      } else {
        reset();
      }
    });
    unlockedRef.current = true;
  }, [blobs]);

  const handlePlay = useCallback(() => {
    if (playing) return;
    ensureUnlocked();
    setPlaying(true);
  }, [playing, ensureUnlocked]);

  const handlePause = useCallback(() => {
    if (playing) setPlaying(false);
  }, [playing]);

  // Salto a un evento arbitrario: forzamos recarga desde cero (loadedIdxRef =
  // -1) y limpiamos cualquier utterance/cronometría TTS pendiente para que el
  // destino arranque limpio. Siempre deja la reproducción andando.
  const jumpTo = useCallback(
    (idx: number) => {
      if (events.length === 0) return;
      const clamped = Math.max(0, Math.min(events.length - 1, idx));
      ensureUnlocked();
      loadedIdxRef.current = -1;
      ttsStepStartRef.current = null;
      ttsStepDurationRef.current = null;
      ttsRemainingRef.current = null;
      if (supportsTts) window.speechSynthesis.cancel();
      setCurrentIdx(clamped);
      setPlaying(true);
    },
    [events.length, ensureUnlocked, supportsTts],
  );

  const handlePrev = useCallback(() => jumpTo(currentIdx - 1), [jumpTo, currentIdx]);
  const handleNext = useCallback(() => jumpTo(currentIdx + 1), [jumpTo, currentIdx]);

  const handleReset = useCallback(() => {
    setPlaying(false);
    setCurrentIdx(0);
    loadedIdxRef.current = -1;
    ttsStepStartRef.current = null;
    ttsStepDurationRef.current = null;
    ttsRemainingRef.current = null;
    stopAudio(userAudioRef.current);
    stopAudio(assistantAudioRef.current);
    if (supportsTts) window.speechSynthesis.cancel();
  }, [supportsTts]);

  const handleClose = useCallback(() => {
    stopAudio(userAudioRef.current);
    stopAudio(assistantAudioRef.current);
    if (supportsTts) window.speechSynthesis.cancel();
    onClose();
  }, [supportsTts, onClose]);

  if (!open) return null;

  const visibleEvents = events.slice(0, Math.min(currentIdx + 1, events.length));
  const isLoading = transcriptQuery.isPending || manifestQuery.isPending;
  const blobsReady = Object.keys(blobs).length;
  const expectedBlobs = manifestQuery.data?.items.length ?? 0;
  // Esperamos a que todos los fetches *resuelvan* (ok o fallidos), no a que todos
  // tengan éxito: un único fetch fallido no debe dejar Play trabado. Los turns sin
  // blob caen al fallback TTS en el efecto de play.
  const stillFetching = expectedBlobs > 0 && settledBlobs < expectedBlobs;
  const progressFrac = events.length > 0 ? Math.min(1, currentIdx / events.length) : 0;

  return (
    <div
      className="mm-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
      data-testid="session-replay-modal"
    >
      <div
        className="mm-dialog replay-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="replay-title"
      >
        <header className="mm-head replay-head">
          <div>
            <h3 id="replay-title" className="mm-title">
              Replay de sesión
            </h3>
            {transcriptQuery.data ? (
              <div className="replay-sub">
                <span className="replay-sub-key">user</span>{' '}
                {transcriptQuery.data.session.userEmail}
                {transcriptQuery.data.session.moduleId ? (
                  <>
                    <span className="replay-sub-sep">·</span>
                    <span className="replay-sub-key">module</span>{' '}
                    {transcriptQuery.data.session.moduleId}
                  </>
                ) : null}
                <span className="replay-sub-sep">·</span>
                <span
                  className={`replay-badge ${
                    hasAnyRealAudio ? 'replay-badge--ok' : 'replay-badge--muted'
                  }`}
                >
                  {hasAnyRealAudio ? 'audio real' : 'audio no grabado · fallback TTS'}
                </span>
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="btn"
            onClick={handleClose}
            data-testid="session-replay-close"
            aria-label="Cerrar replay"
          >
            ✕
          </button>
        </header>

        <div className="mm-body replay-body">
          {isLoading ? (
            <div className="replay-empty">Cargando transcript…</div>
          ) : transcriptQuery.isError ? (
            // 403 gated_module (módulo sensible): placeholder sobrio, no error.
            isGatedModuleError(transcriptQuery.error) ? (
              <div className="replay-empty" data-testid="session-replay-gated">
                🔒 {GATED_CONTENT_PLACEHOLDER}
              </div>
            ) : (
              <div className="mm-error" role="alert">
                {transcriptQuery.error instanceof Error
                  ? transcriptQuery.error.message
                  : 'Error al cargar el replay'}
              </div>
            )
          ) : events.length === 0 ? (
            <div className="replay-empty">
              Esta sesión no tiene turnos con texto persistido. Tracing pudo haber estado apagado.
            </div>
          ) : (
            <>
              <div className="replay-chat" data-testid="replay-chat">
                {visibleEvents.map((ev, idx) => (
                  <button
                    type="button"
                    key={`${ev.kind}-${ev.turnIndex}-${idx}`}
                    className={`replay-bubble replay-bubble--${ev.kind}${
                      idx === currentIdx ? ' replay-bubble--current' : ''
                    }`}
                    onClick={() => jumpTo(idx)}
                    title="Reproducir desde este turno"
                    data-testid={`replay-bubble-${ev.kind}-${ev.turnIndex}`}
                  >
                    <div className="replay-bubble-meta">
                      {ev.kind === 'user' ? 'User' : 'Assistant'} · turn {ev.turnIndex}
                      {ev.audioUrl ? (
                        <span className="replay-bubble-mic" title="audio real persistido">
                          ●
                        </span>
                      ) : null}
                      <span className="replay-bubble-when" title="fecha y hora del turno">
                        {formatStamp(ev.atMs)}
                      </span>
                    </div>
                    <div className="replay-bubble-text">{ev.text}</div>
                  </button>
                ))}
                <div ref={transcriptEndRef} />
              </div>

              <div className="replay-progress" aria-hidden="true">
                <div
                  className="replay-progress-bar"
                  style={{ width: `${progressFrac * 100}%` }}
                />
              </div>
              <div className="replay-time">
                evento {Math.min(currentIdx + 1, events.length)} / {events.length}
                {stillFetching ? <span> · cargando audios ({blobsReady}/{expectedBlobs})</span> : null}
              </div>
            </>
          )}
        </div>

        {/* `<audio>` reusables: uno para user, uno para assistant. Mantenerlos
            como elementos persistentes garantiza que la user-activation que
            otorga el botón Reproducir se conserve para todos los play()
            posteriores en el mismo elemento. */}
        <audio ref={userAudioRef} preload="auto" style={{ display: 'none' }} />
        <audio ref={assistantAudioRef} preload="auto" style={{ display: 'none' }} />

        <footer className="mm-foot replay-foot">
          <div className="replay-foot-info">
            {hasAnyRealAudio
              ? 'Reproduciendo audio real persistido (RECORD_SESSION_AUDIO).'
              : supportsTts
                ? 'Audio no grabado · fallback Web Speech API.'
                : 'Audio no grabado · TTS no soportado en este browser.'}
          </div>
          <div className="replay-controls">
            <button
              type="button"
              className="btn"
              onClick={handleReset}
              disabled={events.length === 0}
              data-testid="session-replay-reset"
            >
              Reiniciar
            </button>
            <button
              type="button"
              className="btn"
              onClick={handlePrev}
              disabled={events.length === 0 || stillFetching || currentIdx <= 0}
              data-testid="session-replay-prev"
              aria-label="Turno anterior"
              title="Turno anterior"
            >
              ‹ Anterior
            </button>
            <button
              type="button"
              className="btn"
              onClick={handleNext}
              disabled={events.length === 0 || stillFetching || currentIdx >= events.length - 1}
              data-testid="session-replay-next"
              aria-label="Turno siguiente"
              title="Turno siguiente"
            >
              Siguiente ›
            </button>
            {playing ? (
              <button
                type="button"
                className="btn-primary"
                onClick={handlePause}
                data-testid="session-replay-pause"
              >
                Pausar
              </button>
            ) : (
              <button
                type="button"
                className="btn-primary"
                onClick={handlePlay}
                disabled={events.length === 0 || stillFetching}
                data-testid="session-replay-play"
              >
                {currentIdx > 0 && currentIdx < events.length ? 'Continuar' : 'Reproducir'}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

function speakFallback(ev: ReplayEvent): void {
  if (!hasSpeechSynthesis()) return;
  const u = new SpeechSynthesisUtterance(ev.text);
  const voices = window.speechSynthesis.getVoices();
  const candidate = pickVoice(voices, ev.kind);
  if (candidate) u.voice = candidate;
  u.rate = 1;
  u.pitch = ev.kind === 'user' ? 1.05 : 0.95;
  window.speechSynthesis.speak(u);
}

function pauseAudio(el: HTMLAudioElement | null): void {
  if (!el) return;
  try {
    el.pause();
  } catch {
    /* ignore */
  }
}

function stopAudio(el: HTMLAudioElement | null): void {
  if (!el) return;
  try {
    el.pause();
    el.currentTime = 0;
    el.removeAttribute('src');
    el.load();
  } catch {
    /* ignore */
  }
}

function pickVoice(
  voices: readonly SpeechSynthesisVoice[],
  kind: 'user' | 'assistant',
): SpeechSynthesisVoice | null {
  if (voices.length === 0) return null;
  const english = voices.filter((v) => v.lang.toLowerCase().startsWith('en'));
  const pool = english.length > 0 ? english : voices;
  const preferred = pool.filter((v) => /google|natural|neural|premium/i.test(v.name));
  const list = preferred.length > 0 ? preferred : pool;
  if (kind === 'assistant') return list[0] ?? null;
  return list[1] ?? list[0] ?? null;
}

